import React, { useEffect, useState } from "react";
import ClientLayout from "../../components/layouts/ClientLayout";
import { auth, firestore } from "../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Activity,
  Shield,
  ArrowUpRight,
  Share2,
  BarChart3,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const CHART_CHECKPOINTS = [
  { label: "Mon", orderScale: 0.25, spendScale: 0.18 },
  { label: "Wed", orderScale: 0.4, spendScale: 0.26 },
  { label: "Fri", orderScale: 0.55, spendScale: 0.34 },
  { label: "Sun", orderScale: 0.7, spendScale: 0.42 },
];
const BASE_SPEND_FLOOR = [120, 180, 240, 320];
const BASE_SPEND_MIN = 60;
const DEFAULT_SPEND_FLOOR = 120;
const DEFAULT_APP_ORIGIN = "https://neuracraft.app";
const CHART_MARGINS = { top: 10, right: 16, left: 0, bottom: 0 };
const INVITE_FEEDBACK_DURATION = 2400;
const AI_SCORE_WEIGHTS = {
  orderWeight: 2.5,
  pendingOrderWeight: 4,
  spendDivisor: 500,
  pendingPaymentBoost: 6,
  clearBoost: 12,
  floor: 18,
};
const HERO_CARD_CLASS =
  "relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-amber-600 to-yellow-400 text-white p-6 shadow-xl";

const ClientDashboard = () => {
  const [clientInfo, setClientInfo] = useState(null);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    totalSpent: 0,
    pendingPayments: 0,
  });

  const [copied, setCopied] = useState(false);

  const user = auth.currentUser;

  const chartData = CHART_CHECKPOINTS.map((checkpoint, index) => {
    const scaledOrders = Math.round(stats.totalOrders * checkpoint.orderScale);
    const scaledSpend = Number(stats.totalSpent) * checkpoint.spendScale;

    return {
      label: checkpoint.label,
      orders: Math.max(
        1,
        Number.isFinite(scaledOrders) ? scaledOrders : index + 2
      ),
      spend: Math.max(
        BASE_SPEND_MIN,
        Number.isFinite(scaledSpend)
          ? scaledSpend
          : BASE_SPEND_FLOOR[index] ?? DEFAULT_SPEND_FLOOR
      ),
    };
  });

  const aiEngagementScore = Math.min(
    100,
    Math.max(
      AI_SCORE_WEIGHTS.floor,
      Math.round(
        stats.totalOrders * AI_SCORE_WEIGHTS.orderWeight +
          stats.pendingOrders * AI_SCORE_WEIGHTS.pendingOrderWeight +
          Number(stats.totalSpent || 0) / AI_SCORE_WEIGHTS.spendDivisor +
          (stats.pendingPayments > 0
            ? AI_SCORE_WEIGHTS.pendingPaymentBoost
            : AI_SCORE_WEIGHTS.clearBoost)
      )
    )
  );

  const handleCopyInvite = async () => {
    const referral = clientInfo?.otherName || clientInfo?.username || "friend";
    const appOrigin =
      typeof window !== "undefined"
        ? "https://neuracraft-378a2.web.app/"
        : process.env.REACT_APP_APP_URL ||
          process.env.REACT_APP_PUBLIC_URL ||
          "https://neuracraft-378a2.web.app/" ||
          process.env.DEFAULT_APP_ORIGIN;
    const link = `${appOrigin}`;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(
          `${link}`
        );
        setCopied(true);
        setTimeout(() => setCopied(false), INVITE_FEEDBACK_DURATION);
      } else {
        alert(`Invite link: ${link}`);
      }
    } catch (error) {
      console.error("Unable to copy invite link", error);
      alert(`Invite link: ${link}`);
    }
  };

  useEffect(() => {
    if (!user) return;

    const loadClient = async () => {
      // Load client Firestore profile (from users collection)
      const userDoc = await getDoc(doc(firestore, "users", user.uid));
      if (!userDoc.exists()) return;

      const data = userDoc.data();
      setClientInfo(data);

      // Load client stats if available in the user document
      if (data.stats) {
        setStats(data.stats);
      }
    };

    loadClient();
  }, [user]);

  return (
    <ClientLayout>
      <div className="p-6 space-y-8 bg-gradient-to-b from-slate-50 via-white to-white rounded-3xl">
        {/* Hero */}
        <div className={HERO_CARD_CLASS}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.18),_transparent_45%)]" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-3 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-semibold">
                <Sparkles className="w-4 h-4" />
                Neura AI Welcome Hub
              </div>
              <h1 className="text-3xl md:text-4xl font-bold">
                Welcome back, {clientInfo?.firstName || "Client"}!
              </h1>
              <p className="text-white/80 text-sm md:text-base leading-relaxed">
                Track your orders, invoices and payments with an AI-inspired cockpit.
                We surface patterns from your ClientPayments, ClientOrders, and ClientInvoices
                so you can act faster and invite teammates on launch day.
              </p>
              <div className="flex flex-wrap gap-3">
                <PulsePill label="Orders logged" value={stats.totalOrders} />
                <PulsePill label="Pending actions" value={stats.pendingOrders + stats.pendingPayments} />
                <PulsePill label="Spend visibility" value={`KES ${stats.totalSpent}`} />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/30 shadow-lg max-w-xs w-full">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  <span className="text-sm font-semibold">AI Confidence</span>
                </div>
                <span className="text-xs bg-black/30 px-2 py-1 rounded-full">
                  Live
                </span>
              </div>
              <div className="text-4xl font-bold leading-tight">
                {aiEngagementScore}%
              </div>
              <p className="text-xs text-white/80 mt-2">
                Based on your spend, open orders, and recent payment signals across companies.
              </p>
              <div className="flex items-center gap-2 text-xs text-white/90 mt-3">
                <ArrowUpRight className="w-4 h-4" />
                Keep momentum by clearing pending payments and confirming deliveries.
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            label="Total Orders"
            value={stats.totalOrders}
            color="from-amber-100 via-white to-amber-50"
            icon={<BarChart3 className="w-5 h-5 text-amber-700" />}
            hint="Across all connected companies"
          />

          <SummaryCard
            label="Pending Orders"
            value={stats.pendingOrders}
            color="from-blue-100 via-white to-blue-50"
            icon={<Activity className="w-5 h-5 text-blue-700" />}
            hint="Ready for confirmation"
          />

          <SummaryCard
            label="Total Spent"
            value={`KES ${stats.totalSpent}`}
            color="from-emerald-100 via-white to-emerald-50"
            icon={<Sparkles className="w-5 h-5 text-emerald-700" />}
            hint="Recent payments recorded"
          />

          <SummaryCard
            label="Pending Payments"
            value={`KES ${stats.pendingPayments}`}
            color="from-purple-100 via-white to-purple-50"
            icon={<Shield className="w-5 h-5 text-purple-700" />}
            hint="Settle to boost trust"
          />
        </div>

        {/* AI Canvas */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-xs text-gray-500 font-semibold">AI Canvas</p>
                <h3 className="text-lg font-bold text-gray-900">
                  Orders & spend rhythm
                </h3>
                <p className="text-sm text-gray-600">
                  Inspired by your ClientPayments, ClientOrders and ClientInvoices history.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
                <Sparkles className="w-4 h-4" />
                Predictive glow
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={CHART_MARGINS}>
                  <defs>
                    <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="orders"
                    stroke="#f59e0b"
                    fillOpacity={1}
                    fill="url(#colorOrders)"
                    name="Orders"
                  />
                  <Area
                    type="monotone"
                    dataKey="spend"
                    stroke="#6366f1"
                    fillOpacity={1}
                    fill="url(#colorSpend)"
                    name="Spend"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-600" />
                <p className="text-sm font-semibold text-gray-800">AI signals</p>
              </div>
              <InsightRow
                title="Momentum"
                value="Keep orders flowing"
                helper={
                  stats.pendingOrders > 0
                    ? `You have ${stats.pendingOrders} orders to approve`
                    : "All orders are clear—great job!"
                }
              />
              <InsightRow
                title="Trust"
                value="Reduce pending payments"
                helper={`KES ${stats.pendingPayments} awaiting settlement`}
              />
              <InsightRow
                title="Savings"
                value="Spot spend peaks"
                helper={`Total spend insight: KES ${stats.totalSpent}`}
              />
            </div>

            <div className="bg-gradient-to-r from-amber-500 via-amber-600 to-orange-500 text-white rounded-2xl p-5 shadow-xl">
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="w-5 h-5" />
                <p className="text-sm font-semibold">Invite friends</p>
              </div>
              <p className="text-sm text-white/90">
                Launch day is better together. Share your personal link and unlock more insights with your circle.
              </p>
              <button
                onClick={handleCopyInvite}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-black/20 hover:bg-black/30 rounded-lg text-sm font-semibold transition"
              >
                {copied ? "Link copied" : "Copy invite link"}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="mt-4 bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Quick Navigation
            </h2>
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Explore the core pages faster
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickButton link="/client/orders" title="My Orders" icon="🛒" />
            <QuickButton link="/client/invoices" title="Invoices" icon="📄" />
            <QuickButton link="/client/payments" title="Payments" icon="💵" />
            <QuickButton link="/client/settings" title="Settings" icon="⚙️" />
          </div>
        </div>

        {/* Latest Activity */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ActivityItem
            title="Orders"
            value={`${stats.totalOrders} total orders placed`}
            icon={<BarChart3 className="w-6 h-6 text-amber-600" />}
          />

          <ActivityItem
            title="Pending"
            value={`${stats.pendingOrders} orders awaiting delivery`}
            icon={<Shield className="w-6 h-6 text-blue-600" />}
          />

          <ActivityItem
            title="Payments"
            value={`KES ${stats.pendingPayments} pending`}
            icon={<Activity className="w-6 h-6 text-purple-600" />}
          />
        </div>
      </div>
    </ClientLayout>
  );
};

// COMPONENTS
const SummaryCard = ({ label, value, color, icon, hint }) => (
  <div className={`rounded-2xl p-4 shadow-lg bg-gradient-to-br ${color} border border-gray-100`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs text-gray-600 font-semibold">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {hint ? <p className="text-xs text-gray-500 mt-1">{hint}</p> : null}
      </div>
      <div className="p-2 bg-white rounded-full shadow-inner border border-gray-100">
        {icon}
      </div>
    </div>
  </div>
);

const QuickButton = ({ title, link, icon }) => (
  <Link
    to={link}
    className="bg-white rounded-xl shadow p-4 flex flex-col items-center hover:shadow-md hover:-translate-y-1 transition border border-gray-100"
  >
    <div className="text-3xl">{icon}</div>
    <p className="text-sm font-medium mt-2">{title}</p>
  </Link>
);

const ActivityItem = ({ title, value, icon }) => (
  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 flex items-center space-x-4">
    <div className="text-2xl flex items-center justify-center w-12 h-12 rounded-full bg-amber-50">
      {icon}
    </div>
    <div>
      <p className="font-semibold text-gray-800">{title}</p>
      <p className="text-sm text-gray-600">{value}</p>
    </div>
  </div>
);

const PulsePill = ({ label, value }) => (
  <div className="px-4 py-2 bg-white/15 backdrop-blur rounded-full text-sm font-semibold border border-white/30">
    <span className="text-white/80">{label}</span>{" "}
    <span className="text-white">{value}</span>
  </div>
);

const InsightRow = ({ title, value, helper }) => (
  <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
    <div className="flex items-center justify-between">
      <p className="text-sm font-semibold text-gray-800">{title}</p>
      <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
        {value}
      </span>
    </div>
    <p className="text-xs text-gray-600 mt-1">{helper}</p>
  </div>
);

export default ClientDashboard;
